import { useState } from 'react';
import { cn } from '../lib/utils';
import { Icon } from './ui/icons';

interface Repository {
  id: string;
  name: string;
  full_name: string;
  description: string;
  language: string;
  risk_score: number;
  compliance_score: number;
  controls_passing: number;
  controls_total: number;
  last_scan: string;
  status: 'healthy' | 'warning' | 'critical';
  project: string;
}

interface RepositoriesProps {
  navigate: (route: string) => void;
}

const mockRepositories: Repository[] = [
  {
    id: '1',
    name: 'cylift-dashboard',
    full_name: 'cylift/cylift-dashboard',
    description: 'Unified security and compliance dashboard',
    language: 'TypeScript',
    risk_score: 15,
    compliance_score: 92,
    controls_passing: 23,
    controls_total: 25,
    last_scan: '2024-05-22T10:30:00Z',
    status: 'healthy',
    project: 'CyLift Dashboard'
  },
  {
    id: '2',
    name: 'api-gateway',
    full_name: 'platform/api-gateway',
    description: 'Central API gateway for microservices',
    language: 'Go',
    risk_score: 28,
    compliance_score: 87,
    controls_passing: 18,
    controls_total: 22,
    last_scan: '2024-05-22T09:15:00Z',
    status: 'warning',
    project: 'API Gateway Services'
  },
  {
    id: '3', 
    name: 'data-processor',
    full_name: 'data/data-processor',
    description: 'ETL pipeline for security events',
    language: 'Python',
    risk_score: 42,
    compliance_score: 78,
    controls_passing: 14,
    controls_total: 20,
    last_scan: '2024-05-22T08:45:00Z',
    status: 'critical',
    project: 'Data Processing Pipeline'
  },
  {
    id: '4',
    name: 'mobile-app',
    full_name: 'mobile/customer-app',
    description: 'Customer-facing mobile application',
    language: 'TypeScript',
    risk_score: 8,
    compliance_score: 95,
    controls_passing: 19,
    controls_total: 20,
    last_scan: '2024-05-22T11:00:00Z',
    status: 'healthy',
    project: 'Mobile Application'
  },
  {
    id: '5',
    name: 'auth-service',
    full_name: 'platform/auth-service',
    description: 'Authentication and authorization service',
    language: 'Rust',
    risk_score: 12,
    compliance_score: 89,
    controls_passing: 21,
    controls_total: 24,
    last_scan: '2024-05-22T07:20:00Z',
    status: 'healthy',
    project: 'API Gateway Services'
  }
];

const languageColors: Record<string, string> = {
  TypeScript: '#3178C6',
  JavaScript: '#F1E05A',
  Go: '#00ADD8',
  Python: '#3572A5',
  Rust: '#DEA584',
  Java: '#ED8B00'
};

function RepositoryRow({ repo, onClick }: { repo: Repository; onClick: () => void }) {
  const statusColors = {
    healthy: 'text-success-600',
    warning: 'text-warning-600',
    critical: 'text-danger-600'
  };

  const riskColor = repo.risk_score >= 35 ? 'bg-danger-500' : 
                    repo.risk_score >= 20 ? 'bg-warning-500' : 
                    'bg-success-500';

  return (
    <tr 
      onClick={onClick}
      className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer transition"
    >
      <td className="py-3 pr-4">
        <div className="flex items-center gap-3">
          <Icon name="book" size={16} className="text-ink/60" />
          <div>
            <div className="font-display font-semibold text-ink text-[14px]">{repo.name}</div>
            <div className="font-data text-[12px] text-ink/55">{repo.full_name}</div>
          </div>
        </div>
      </td>
      <td className="px-3 py-3">
        <div className="flex items-center gap-2">
          <span 
            className="h-3 w-3 rounded-full"
            style={{ backgroundColor: languageColors[repo.language] || '#666' }}
          />
          <span className="font-data text-[13px] text-ink">{repo.language}</span>
        </div>
      </td>
      <td className="px-3 py-3">
        <div className="flex items-center gap-2">
          <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className={cn('h-full rounded-full', riskColor)}
              style={{ width: `${Math.min(100, repo.risk_score * 2)}%` }}
            />
          </div>
          <span className="font-display font-bold text-ink text-[13px] w-8 text-right">
            {repo.risk_score}
          </span>
        </div>
      </td>
      <td className="px-3 py-3">
        <span className={cn('font-display font-semibold text-[13px]', statusColors[repo.status])}>
          {repo.compliance_score}%
        </span>
      </td>
      <td className="px-3 py-3">
        <span className="font-data text-[13px] text-ink">
          {repo.controls_passing}/{repo.controls_total}
        </span>
      </td>
      <td className="px-3 py-3">
        <span className="font-data text-[12px] text-ink/55">
          {new Date(repo.last_scan).toLocaleString()}
        </span>
      </td>
      <td className="pl-3 py-3">
        <Icon name="chevron-right" size={16} className="text-ink/40" />
      </td>
    </tr>
  );
}

export default function Repositories({ navigate }: RepositoriesProps) {
  const [repositories] = useState<Repository[]>(mockRepositories);
  const [filter, setFilter] = useState<'all' | 'healthy' | 'warning' | 'critical'>('all');
  const [sortBy, setSortBy] = useState<'name' | 'risk_score' | 'compliance_score'>('risk_score');

  const filteredRepos = repositories.filter(repo => 
    filter === 'all' || repo.status === filter
  );

  const sortedRepos = [...filteredRepos].sort((a, b) => {
    if (sortBy === 'name') return a.name.localeCompare(b.name);
    if (sortBy === 'risk_score') return b.risk_score - a.risk_score;
    if (sortBy === 'compliance_score') return b.compliance_score - a.compliance_score;
    return 0;
  });

  return (
    <div className="flex flex-col gap-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-6">
        <div>
          <h1 className="font-display font-bold text-ink" style={{ fontSize: 36, lineHeight: 1.05 }}>
            Repositories
          </h1>
          <p className="font-display text-[18px] font-medium text-ink/70 mt-2">
            Monitor security compliance across all your repositories.
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <select 
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className="px-3 py-2 border border-gray-200 rounded-lg font-display text-[14px]"
          >
            <option value="risk_score">Sort by Risk Score</option>
            <option value="compliance_score">Sort by Compliance</option>
            <option value="name">Sort by Name</option>
          </select>
          
          <button
            onClick={() => navigate('repositories/scan')}
            className="h-10 px-4 rounded-[10px] bg-primary-500 text-white font-display font-bold text-[15px] inline-flex items-center gap-2 hover:bg-primary-600 transition"
          >
            <Icon name="refresh" size={16} />
            Scan All
          </button>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200">
        {[
          { id: 'all', label: 'All Repositories', count: repositories.length },
          { id: 'healthy', label: 'Healthy', count: repositories.filter(r => r.status === 'healthy').length },
          { id: 'warning', label: 'Warning', count: repositories.filter(r => r.status === 'warning').length },
          { id: 'critical', label: 'Critical', count: repositories.filter(r => r.status === 'critical').length }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setFilter(tab.id as any)}
            className={cn(
              'px-4 py-2 font-display text-[14px] font-medium border-b-2 transition',
              filter === tab.id
                ? 'text-primary-600 border-primary-500'
                : 'text-ink/60 border-transparent hover:text-ink hover:border-gray-300'
            )}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {/* Repositories Table */}
      <div className="rounded-[10px] border border-gray-200 bg-white overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr className="font-display text-[12px] font-bold uppercase tracking-[0.04em] text-ink/55">
              <th className="text-left py-3 pr-4 pl-5">Repository</th>
              <th className="text-left px-3 py-3">Language</th>
              <th className="text-left px-3 py-3">Risk Score</th>
              <th className="text-left px-3 py-3">Compliance</th>
              <th className="text-left px-3 py-3">Controls</th>
              <th className="text-left px-3 py-3">Last Scan</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {sortedRepos.map(repo => (
              <RepositoryRow
                key={repo.id}
                repo={repo}
                onClick={() => navigate(`repositories/${repo.id}`)}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Empty State */}
      {sortedRepos.length === 0 && (
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <Icon name="book" size={48} className="text-ink/20 mb-4" />
          <h3 className="font-display font-semibold text-ink text-[18px] mb-2">
            No {filter !== 'all' && filter} repositories found
          </h3>
          <p className="font-display text-[14px] text-ink/60 max-w-md">
            {filter === 'all' 
              ? "Connect your repositories to start monitoring security compliance."
              : `No repositories with ${filter} status at the moment.`
            }
          </p>
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="p-4 rounded-lg bg-success-50 border border-success-200">
          <div className="font-display font-bold text-success-600 text-[24px]">
            {repositories.filter(r => r.status === 'healthy').length}
          </div>
          <div className="font-display text-[14px] text-success-600 font-medium">Healthy Repositories</div>
        </div>
        
        <div className="p-4 rounded-lg bg-warning-50 border border-warning-200">
          <div className="font-display font-bold text-warning-600 text-[24px]">
            {repositories.filter(r => r.status === 'warning').length}
          </div>
          <div className="font-display text-[14px] text-warning-600 font-medium">Need Attention</div>
        </div>
        
        <div className="p-4 rounded-lg bg-danger-50 border border-danger-200">
          <div className="font-display font-bold text-danger-600 text-[24px]">
            {repositories.filter(r => r.status === 'critical').length}
          </div>
          <div className="font-display text-[14px] text-danger-600 font-medium">Critical Issues</div>
        </div>
        
        <div className="p-4 rounded-lg bg-primary-50 border border-primary-200">
          <div className="font-display font-bold text-primary-600 text-[24px]">
            {Math.round(repositories.reduce((sum, r) => sum + r.compliance_score, 0) / repositories.length)}%
          </div>
          <div className="font-display text-[14px] text-primary-600 font-medium">Average Compliance</div>
        </div>
      </div>
    </div>
  );
}